// ═══════════════════════════════════════════════════════════════════════════
// AdminTeamPanel — Team tab inside AdminPage.
//
// Admin-only surface for granting + revoking elevated access to the CMS.
// Lives as a tab on AdminPage (only renders for users where isAdmin
// is true; managers see this tab hidden via the AdminPage gate).
//
// Flow:
//   1. Type an email → "Look up" → resolves to an existing user via
//      find_user_by_email RPC, or shows "Not on Epio yet, have them
//      sign up first."
//   2. Choose role (manager or admin) + tick scopes (managers only).
//   3. Save → upserts to user_roles. Existing row gets updated.
//
// Table below shows every role row with email, name, role, scopes,
// and Revoke action. Auto-refreshes after every assign/revoke.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useState } from 'react';
import {
  listRoleRows,
  findUserByEmail,
  assignRole,
  removeRole,
  SCOPES,
} from '../lib/roles';
import { useAuth } from '../components/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Spinner from '../components/shared/Spinner';

export default function AdminTeamPanel() {
  const { user } = useAuth();
  const toast = useToast();

  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite-flow state
  const [email, setEmail] = useState('');
  const [lookup, setLookup] = useState(null);     // resolved user row from find_user_by_email
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [role, setRole] = useState('manager');
  const [scopes, setScopes] = useState([]);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setRows(await listRoleRows()); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // ── Invite handlers ─────────────────────────────────────────────────────
  async function handleLookup() {
    const clean = email.trim().toLowerCase();
    setLookup(null);
    setLookupError('');
    if (!clean) { setLookupError('Enter an email.'); return; }
    setLookupBusy(true);
    try {
      const found = await findUserByEmail(clean);
      if (found) {
        setLookup(found);
        // Sensible defaults for a brand-new manager — content scope
        // is what most invites are for (CMS curation work).
        setRole('manager');
        setScopes(['content']);
      } else {
        setLookupError(
          `No Epio account for ${clean}. Have them sign up first, then come back.`
        );
      }
    } catch (e) {
      setLookupError(e?.message || 'Lookup failed.');
    } finally {
      setLookupBusy(false);
    }
  }

  async function handleAssign() {
    if (!lookup) return;
    setSaving(true);
    try {
      await assignRole(lookup.user_id, role, role === 'admin' ? [] : scopes);
      toast.success(`Role set: ${displayName(lookup)} is now ${role}${role === 'manager' && scopes.length ? ` (${scopes.join(', ')})` : ''}`);
      // Reset form + reload table
      setEmail('');
      setLookup(null);
      setScopes([]);
      reload();
      // If the admin granted a role to themselves (rare, but possible
      // for the initial admin updating their own row), refresh the
      // auth context's role so menus update immediately.
      if (lookup.user_id === user?.id) {
        try { window.dispatchEvent(new Event('role:refresh')); } catch {}
      }
    } catch (e) {
      toast.error(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(row) {
    if (!window.confirm(`Revoke ${row.role} from ${displayName(row)}?`)) return;
    try {
      await removeRole(row.user_id);
      toast.success(`Revoked role from ${displayName(row)}`);
      reload();
    } catch (e) {
      toast.error(e?.message || 'Revoke failed.');
    }
  }

  function toggleScope(id) {
    setScopes(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  }

  return (
    <div className="admin-team">
      <h2 className="admin-team-title">Team</h2>
      <p className="admin-team-intro">
        Grant elevated access to the CMS. Admins get full control. Managers
        only see the sections you scope them to — Users, Support, Content.
      </p>

      {/* ─── Invite flow ─── */}
      <div className="admin-team-invite">
        <div className="admin-team-row">
          <input
            type="email"
            className="admin-team-input"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
            disabled={lookupBusy}
          />
          <button
            type="button"
            className="admin-team-btn admin-team-btn--ghost"
            onClick={handleLookup}
            disabled={lookupBusy || !email.trim()}
          >
            {lookupBusy ? 'Looking up…' : 'Look up'}
          </button>
        </div>

        {lookupError && (
          <div className="admin-team-error">{lookupError}</div>
        )}

        {lookup && (
          <div className="admin-team-form">
            <div className="admin-team-found">
              <strong>{displayName(lookup)}</strong>{' '}
              <span className="admin-team-found-email">{lookup.email}</span>
            </div>

            <div className="admin-team-field">
              <label className="admin-team-label">Role</label>
              <div className="admin-team-radio-row">
                <label className="admin-team-radio">
                  <input
                    type="radio"
                    name="role"
                    value="manager"
                    checked={role === 'manager'}
                    onChange={() => setRole('manager')}
                  />
                  <span>Manager</span>
                </label>
                <label className="admin-team-radio">
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={role === 'admin'}
                    onChange={() => setRole('admin')}
                  />
                  <span>Admin</span>
                </label>
              </div>
              {role === 'admin' && (
                <div className="admin-team-hint">
                  Admins have full access to every CMS section and can manage other roles. Skip the scopes below.
                </div>
              )}
            </div>

            {role === 'manager' && (
              <div className="admin-team-field">
                <label className="admin-team-label">Scopes</label>
                <div className="admin-team-scopes">
                  {SCOPES.map((s) => (
                    <label key={s.id} className="admin-team-scope">
                      <input
                        type="checkbox"
                        checked={scopes.includes(s.id)}
                        onChange={() => toggleScope(s.id)}
                      />
                      <span>
                        <strong>{s.label}</strong>
                        <span className="admin-team-scope-desc">{s.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {scopes.length === 0 && (
                  <div className="admin-team-hint admin-team-hint--warn">
                    A manager with zero scopes can't see anything in the CMS — pick at least one.
                  </div>
                )}
              </div>
            )}

            <div className="admin-team-actions">
              <button
                type="button"
                className="admin-team-btn admin-team-btn--ghost"
                onClick={() => { setLookup(null); setEmail(''); setScopes([]); }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-team-btn admin-team-btn--primary"
                onClick={handleAssign}
                disabled={saving || (role === 'manager' && scopes.length === 0)}
              >
                {saving ? 'Saving…' : 'Save role'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Current role table ─── */}
      <h3 className="admin-team-subtitle">Current team</h3>
      {loading ? (
        <div className="admin-team-loading"><Spinner center /></div>
      ) : rows.length === 0 ? (
        <div className="admin-team-empty">No team members yet. Invite one above.</div>
      ) : (
        <div className="admin-team-list">
          {rows.map((r) => (
            <div key={r.user_id} className="admin-team-list-row">
              <div className="admin-team-list-main">
                <div className="admin-team-list-name">{displayName(r)}</div>
                <div className="admin-team-list-email">{r.email}</div>
              </div>
              <div className="admin-team-list-meta">
                <span className={`admin-team-pill admin-team-pill--${r.role}`}>{r.role}</span>
                {r.role === 'manager' && (
                  <span className="admin-team-scopes-inline">
                    {Array.isArray(r.scopes) && r.scopes.length > 0
                      ? r.scopes.join(' · ')
                      : <em>no scopes</em>}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="admin-team-btn admin-team-btn--danger"
                onClick={() => handleRevoke(r)}
                disabled={r.user_id === user?.id && r.role === 'admin'}
                title={r.user_id === user?.id && r.role === 'admin' ? "Can't revoke yourself" : undefined}
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function displayName(row) {
  return (row?.full_name || row?.username || row?.email || 'Unknown').trim();
}
